import React, { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import styles from './pagination.module.scss';

const ELLIPSIS = '…';

function range(start: number, end: number) {
  const result: number[] = [];
  for (let i = start; i <= end; i += 1) result.push(i);
  return result;
}

const Pagination = ({
  totalItems,
  itemsPerPage,
  currentPage,
}:{
  totalItems: number,
  itemsPerPage: number,
  currentPage: number,
}) => {
  const safeItemsPerPage = Math.max(1, Math.floor(Number(itemsPerPage)) || 1);
  const safeCurrentPage = Math.max(1, Math.floor(Number(currentPage)) || 1);
  const totalPages = Math.ceil(totalItems / safeItemsPerPage);
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();

  const pagesToRender = useMemo<(number | typeof ELLIPSIS)[]>(() => {
    if (totalPages <= 1) return [];

    const siblingCount = 2; // pages around current
    const firstPage = 1;
    const lastPage = totalPages;

    const leftSibling = Math.max(safeCurrentPage - siblingCount, firstPage);
    const rightSibling = Math.min(safeCurrentPage + siblingCount, lastPage);

    const shouldShowLeftEllipsis = leftSibling > firstPage + 1;
    const shouldShowRightEllipsis = rightSibling < lastPage - 1;

    // Always show first/last; show window around current; add ellipses when needed
    if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      const leftRange = range(firstPage, Math.min(rightSibling + 1, lastPage));
      return [...leftRange, ELLIPSIS, lastPage];
    }

    if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
      const rightRange = range(Math.max(leftSibling - 1, firstPage), lastPage);
      return [firstPage, ELLIPSIS, ...rightRange];
    }

    if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      const middleRange = range(leftSibling, rightSibling);
      return [firstPage, ELLIPSIS, ...middleRange, ELLIPSIS, lastPage];
    }

    return range(firstPage, lastPage);
  }, [safeCurrentPage, totalPages]);

  const updateUrl = (page: number) => {
    const searchParams = new URLSearchParams(location.search);
    const nextPage = Math.floor(page);
    const skip = (nextPage - 1) * safeItemsPerPage;
    searchParams.set('skip', String(skip));
    searchParams.set('page', String(nextPage));
    searchParams.set('limit', String(safeItemsPerPage));
    setSearchParams(new URLSearchParams(searchParams));
  };

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      updateUrl(page);
    }
  };

  if (totalPages <= 1) return null;

  return (
    <div className={styles.pagination}>
      <button
        className={styles.nav}
        disabled={safeCurrentPage <= 1}
        onClick={() => handlePageClick(safeCurrentPage - 1)}
      >
        Précédent
      </button>

      <div className={styles.pages}>
        {pagesToRender.map((p, idx) => {
          if (p === ELLIPSIS) {
            return (
              <span key={`${ELLIPSIS}-${idx}`} className={styles.ellipsis}>
                {ELLIPSIS}
              </span>
            );
          }
          return (
            <button
              key={p}
              className={`${p === safeCurrentPage ? styles.active : ''}`}
              onClick={() => handlePageClick(p)}
            >
              {p}
            </button>
          );
        })}
      </div>

      <button
        className={styles.nav}
        disabled={safeCurrentPage >= totalPages}
        onClick={() => handlePageClick(safeCurrentPage + 1)}
      >
        Suivant
      </button>
    </div>
  );
};

export default Pagination;
